import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowLeft,
  ShieldCheck, 
  Wallet, 
  Receipt, 
  Gift, 
  CreditCard, 
  Heartbeat,
  CheckCircle,
  CaretRight,
  Brain,
  Warning,
  Hospital,
  Stethoscope,
  Pill
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Layout } from '../../components/layout/Layout';
import { 
  LiquidGlassBg, 
  MacbookFrame, 
  IphoneFrame 
} from "../../components/ui/LiquidGlassComponents";

const railsData = {
  'opd': {
    icon: Hospital,
    title: 'OPD Rail',
    headline: 'Cashless Outpatient Infrastructure',
    description: 'Transform how your employees access primary care. Our OPD infrastructure provides 100% cashless execution across a nationwide network of clinics and hospitals.',
    benefits: [
      'Cashless Execution: Zero out-of-pocket stress for employees.',
      'Real-time Tracking: Departmental utilization visibility.',
      'Dynamic Pricing: Integrated negotiated rates for corporate clients.',
      'Network Management: Direct connectivity to 300+ partner hospitals.',
    ],
    outcomes: [
      '65% Reduction in employee support tickets for OPD claims.',
      '100% Transparency in outpatient spend and network utilization.',
      'Immediate access to care without reimbursement cycles.',
    ],
    ctas: [
      { label: 'Register Your Company', href: '/register', primary: true },
      { label: 'Request Demo', href: '/demo', primary: false },
    ],
    color: 'blue',
  },
  'ipd': {
    icon: Stethoscope,
    title: 'IPD Rail',
    headline: 'Streamlined Inpatient Governance',
    description: 'Autonomous management of hospitalization and surgery. Our IPD rail automates pre-authorizations and adjudication to ensure rapid, governed care for your workforce.',
    benefits: [
      'Pre-auth Automation: Digital approval cycles in under 30 minutes.',
      'Claim Adjudication: AI-driven verification of clinical necessity.',
      'Hospital Relations: Direct direct settlement protocols with Top-tier providers.',
      'Case Management: Active clinical oversight during hospitalization.',
    ],
    outcomes: [
      '40% Faster hospitalization authorization processing.',
      'Significant reduction in adjudication errors and leakage.',
      'Consistently high employee satisfaction during critical health events.',
    ],
    ctas: [
      { label: 'Explore IPD Rules', href: '/demo', primary: true },
      { label: 'Consult Expert', href: '/demo', primary: false },
    ],
    color: 'blue',
  },
  'pharmacy': {
    icon: Pill,
    title: 'Pharmacy Rail',
    headline: 'Direct-to-Patient Fulfillment Infrastructure',
    description: 'Eliminate manual pharmacy reimbursement. Our infrastructure integrates nationwide fulfillment centers to deliver medications directly to employees with full formulary control.',
    benefits: [
      'E-prescription Sync: Digital linkage between doctor and delivery.',
      'Home Delivery: Nationwide reach for chronic and acute fulfillment.',
      'Formulary Control: Rule-based governance on allowed medications.',
      'Inventory Insights: Data on the most common medications in your organization.',
    ],
    outcomes: [
      '15-20% Savings on medication spend through direct fulfillment.',
      '100% Compliance with corporate health policies and drug lists.',
      'Zero-paperwork experience for employee chronic care management.',
    ],
    ctas: [
      { label: 'Setup Pharmacy Rail', href: '/register', primary: true },
      { label: 'View Fulfillment Network', href: '/network', primary: false },
    ],
    color: 'blue',
  },
  'insurance': {
    icon: ShieldCheck,
    title: 'Insurance Rail',
    headline: 'Autonomous Corporate Insurance Governance',
    description: 'Transform your corporate insurance from a fixed expense into a dynamic, data-driven utility. Our infrastructure eliminates broker fragmentation and provides real-time transparency across providers.',
    benefits: [
      'Direct-to-Provider Connectivity: Remove broker opacity and hidden commissions.',
      'Real-time Premium Comparison: Side-by-side analysis of Takaful and conventional models.',
      'AI-Driven Renewals: Automated pattern detection for optimized policy structuring.',
      'Digital Governance: Complete audit trail from activation to premium settlement.',
      'SECP Validated TPA Framework: Licensed governance for institutional-grade reliability.',
    ],
    outcomes: [
      '15-20% Immediate premium optimization through direct negotiation.',
      'Zero-Broker transparency with 100% audit-ready reporting.',
      'Shift from fixed annual costs to active risk management.',
    ],
    ctas: [
      { label: 'Compare Insurance Models', href: '/calculators/insurance', primary: true },
      { label: 'Request Infrastructure Demo', href: '/demo', primary: false },
    ],
    color: 'blue',
  },
  'wallet-pool': {
    icon: Wallet,
    title: 'Wallet-Based Pool',
    headline: 'High-Liquidity Corporate Health Wallets',
    description: 'Finance your healthcare autonomously. Our wallet infrastructure provides full liquidity control, allowing employers to fund pools dynamically while retaining unused capital.',
    benefits: [
      "Dynamic Liquidity Control: Fund what you need, withdraw what you don't.",
      'Departmental Granularity: Custom limits and burn rates for specific teams.',
      'Real-time Burn Monitoring: Predict exposure before it happens.',
      'Modular Rule Engine: Configure OPD and IPD policies on the fly.',
      'Cashless Network Integration: Direct settlement from wallet to provider.',
    ],
    outcomes: [
      '100% Capital liquidity—no more funds trapped in rigid insurance pools.',
      'Real-time visibility into actual health spend vs. allocation.',
      'Significant reduction in administration overhead for pool management.',
    ],
    ctas: [
      { label: 'Explore Wallet Model', href: '/calculators/quick', primary: true },
      { label: 'Request Demo', href: '/demo', primary: false },
    ],
    color: 'green',
  },
  'reimbursement': {
    icon: Receipt,
    title: 'Reimbursement Rail',
    headline: 'AI-Powered Digital Reimbursement Infrastructure',
    description: 'Eliminate paper-heavy processes and manual fraud. Our reimbursement infrastructure uses AI-driven governance to automate approvals and ensure 100% policy compliance.',
    benefits: [
      'Paperless Bill Scanning: AI-powered OCR for instant bill verification.',
      'Autonomous Compliance Engine: Scale-matched policy validation at submission.',
      'Seamless Payout Integration: Direct connectivity to payroll systems.',
      'Audit-Ready Trails: Every submission and approval timestamped and logged.',
      'Hybrid Approval Cycles: Choose between autonomous or manual HR sign-offs.',
    ],
    outcomes: [
      '8-12% Reduction in leakage through digital governance and AI verification.',
      '60% Gain in HR administrative efficiency for health claim processing.',
      'Immediate employee satisfaction with faster, transparent payouts.',
    ],
    ctas: [
      { label: 'Calculate Admin Savings', href: '/calculators/quick', primary: true },
      { label: 'Request Live Demo', href: '/demo', primary: false },
    ],
    color: 'purple',
  },
  'zero-budget': {
    icon: Gift,
    title: 'Zero Budget Corporate Access',
    headline: 'Curated Health Value with Zero Budget Exposure',
    description: 'Provide institutional-grade healthcare access to your employees without any financial commitment. Leverage our nationwide infrastructure for negotiated discounts and services.',
    benefits: [
      'Zero Premium Exposure: No upfront budget or financial risk required.',
      'Institutional Discounts: Employees access negotiated rates (15-25%) at top providers.',
      'Telemedicine on Demand: 24/7 access to qualified doctors via mobile app.',
      'Cashless Connectivity: Employees top up personal wallets to use our network.',
      'Rapid Activation: Go live across your organization in under 24 hours.',
    ],
    outcomes: [
      'HR delivers high-impact health benefits with zero budget impact.',
      'Immediate improvement in employee health literacy and access.',
      'Flexible stepping stone toward more complex health infrastructure.',
    ],
    ctas: [
      { label: 'Activate Free Access', href: '/register', primary: true },
      { label: 'See Employee Savings', href: '/calculators/quick', primary: false },
    ],
    color: 'orange',
  },
  'health-credit': {
    icon: CreditCard,
    title: 'Health Credit Rail',
    headline: 'Governed Credit Facilities for Healthcare Continuity',
    description: 'Bridge the gap between care and capital. Our credit infrastructure provides KYC-governed facilities for employees, ensuring timely care while managing corporate exposure.',
    benefits: [
      'KYC-Governed Facilities: Secure eligibility assessment for corporate credit lines.',
      'Salary-Linked Recovery: Automated settlement via payroll integration.',
      'Network-Restricted Usage: Credit can only be spent at approved health providers.',
      'Flexible Exposure Cycles: 30 to 60-day repayment windows for corporations.',
      'Capital Smoothing: Manage seasonal health spend spikes without cash flow stress.',
    ],
    outcomes: [
      'Ensured care continuity for employees during high-cost health events.',
      'Improved corporate cash flow flexibility through goverened credit.',
      'Controlled risk with platform-enforced usage protocols.',
    ],
    ctas: [
      { label: 'Verify Eligibility', href: '/register', primary: true },
      { label: 'Request Demo', href: '/demo', primary: false },
    ],
    color: 'cyan',
  },
  'wellness': {
    icon: Heartbeat,
    title: 'Wellness & Preventive Rail',
    headline: 'Measurable Prevention. Optimized Exposure.',
    description: 'Shift from reactive treatment to proactive health governance. Our wellness infrastructure integrates preventive care measurements directly into your financial rail.',
    benefits: [
      'Predictive Risk Profiling: Departmental health indicators and risk detection.',
      'Home-Sampling Integration: Nationwide lab coverage for regular screening.',
      'Tele-Consultation Stack: Mobile-first primary care to reduce hospitalization.',
      'HRA Driven Insights: Real-time surveys that inform benefit structure changes.',
      'Measurable Outcomes: Track how wellness spend reduces long-term costs.',
    ],
    outcomes: [
      'Reduced high-cost IPD exposure through early detection and primary care.',
      'Measurable improvement in organizational productivity and attendance.',
      'Data-driven verification of wellness ROI for executive reporting.',
    ],
    ctas: [
      { label: 'Launch Wellness Rail', href: '/register', primary: true },
      { label: 'Request Live Demo', href: '/demo', primary: false },
    ],
    color: 'pink',
  },
};

const colorClasses = {
  blue: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
  green: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
  purple: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
  orange: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
  cyan: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
  pink: { bg: 'bg-[#0048ff]', light: 'bg-[#0048ff]/10', text: 'text-[#0048ff]' },
};

export default function BenefitRailPage() {
  const { railId } = useParams();
  const rail = railsData[railId];

  if (!rail) {
    return (
      <Layout>
        <div className="section-white flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Warning size={64} className="text-red-500 mb-6" weight="fill" />
          <h1 className="heading-section mb-4">Infrastructure Module Not Found</h1>
          <p className="body-text mb-8">The requested benefit rail is either being updated or does not exist in our current framework.</p>
          <Link to="/">
            <Button size="lg" className="bg-[#0048ff]">
              Return to Control Center
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const colors = colorClasses[rail.color];
  const IconComponent = rail.icon;

  return (
    <Layout>
      {/* Dynamic Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-[#F8F9FB]">
        <LiquidGlassBg />
        
        <div className="container-wide relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#11111f]/40 hover:text-[#0048ff] mb-12 transition-all font-bold text-xs uppercase tracking-widest group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Back to Infrastructure Overview
          </Link>

          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-xl ${colors.light} flex items-center justify-center`}>
                  <IconComponent size={28} className={colors.text} weight="duotone" />
                </div>
                <span className={`label-text ${colors.text} !text-xs`}>
                  {rail.title} Module
                </span>
              </div>

              <h1 className="text-[40px] md:text-[52px] lg:text-[60px] font-bold leading-[1.1] mb-8 text-[#11111f]">
                {rail.headline}
              </h1>
              
              <p className="body-text text-xl mb-12 text-[#11111f]/80 max-w-xl border-l-4 border-[#0048ff] pl-6 py-2 bg-gradient-to-r from-[#0048ff]/5 to-transparent">
                {rail.description}
              </p>

              <div className="flex flex-wrap gap-4">
                {rail.ctas.map((cta, idx) => (
                  <Link key={idx} to={cta.href} className="w-full sm:w-auto">
                    <Button 
                      size="lg" 
                      className={`w-full sm:w-auto h-16 px-10 rounded-2xl text-lg font-bold transition-all ${
                        cta.primary 
                        ? `${colors.bg} hover:opacity-90 shadow-xl shadow-[#0048ff]/20` 
                        : 'border-[#E5E7EB] hover:bg-[#F8F9FB]'
                      }`} 
                      variant={cta.primary ? 'default' : 'outline'}
                    >
                      {cta.label}
                      {cta.primary && <ArrowRight size={20} className="ml-2" weight="bold" />}
                    </Button>
                  </Link>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative hidden lg:block"
            >
              <MacbookFrame className="shadow-3xl border-[#E5E7EB]">
                <div className="p-8 bg-white min-h-[440px] flex items-center justify-center relative overflow-hidden group">
                  <IconComponent size={300} className={`${colors.text} opacity-5 group-hover:scale-110 transition-transform duration-1000`} weight="thin" />
                  
                  {/* Mockup Overlay Components */}
                  <div className="absolute inset-0 p-8 grid grid-cols-2 gap-4">
                    <motion.div 
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className="p-6 rounded-2xl shadow-xl border border-white/20 h-fit liquid-glass bg-white/60"
                    >
                      <div className={`w-10 h-10 rounded-lg ${colors.light} flex items-center justify-center mb-4 text-${rail.color}-500 shadow-sm border border-white/20`}>
                        <IconComponent size={24} weight="bold" />
                      </div>
                      <div className="h-2 w-3/4 bg-[#11111f]/10 rounded mb-2" />
                      <div className="h-2 w-1/2 bg-[#11111f]/10 rounded" />
                    </motion.div>

                    <motion.div 
                      animate={{ y: [0, 10, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                      className="bg-[#0A1128] p-6 rounded-2xl shadow-2xl h-fit mt-20 border border-white/10"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white">
                          <Brain size={24} />
                        </div>
                        <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Active Governance</div>
                      </div>
                      <div className="h-1.5 w-full bg-white/10 rounded mb-2" />
                      <div className="h-1.5 w-2/3 bg-white/10 rounded" />
                    </motion.div>
                  </div>
                </div>
              </MacbookFrame>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Capabilities Section */}
      <section className="section-gray relative">
        <div className="container-wide">
          <div className="text-center mb-20">
            <span className={`label-text ${colors.text} mb-4 block`}>Strategic Capabilities</span>
            <h2 className="heading-section !text-center">How the {rail.title} Functions.</h2>
            <p className="subheading-section mt-4">Embedded governance and real-time control built for institutional scale.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rail.benefits.map((benefit, idx) => {
              const [title, desc] = benefit.split(':');
              return (
                <motion.div
                  key={idx}
                  className="bg-white p-8 rounded-3xl border border-[#E5E7EB] hover:border-[#0048ff]/20 hover:shadow-2xl hover:shadow-[#0048ff]/5 transition-all group liquid-glass"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className={`w-12 h-12 rounded-xl ${colors.light} flex items-center justify-center mb-6 border border-[#0048ff]/5 group-hover:bg-[#0048ff] group-hover:text-white transition-all`}>
                    <CheckCircle size={24} weight="bold" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#11111f]">{title}</h3>
                  <p className="text-[#11111f]/60 text-sm leading-relaxed font-medium">{desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Financial Outcomes Section */}
      <section className="section-white overflow-hidden relative">
        {/* Decorative numbers bg */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none select-none overflow-hidden flex flex-wrap gap-20 p-20">
          {[...Array(10)].map((_, i) => <span key={i} className="text-[200px] font-bold text-[#0048ff]">{i + 1}</span>)}
        </div>

        <div className="container-wide relative z-10">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="lg:w-1/3">
              <span className={`label-text ${colors.text} mb-4 block`}>Financial Impact</span>
              <h2 className="text-4xl font-bold mb-6 text-left mx-0">Measurable Outcomes.</h2>
              <p className="body-text text-lg">We define success through hard data. Each implementation of the {rail.title} is designed to deliver visible financial and operational ROI.</p>
            </div>
            
            <div className="lg:w-2/3 grid sm:grid-cols-2 gap-6 w-full">
              {rail.outcomes.map((outcome, idx) => (
                <motion.div
                  key={idx}
                  className="bg-[#F8F9FB] p-8 rounded-3xl border border-transparent hover:border-[#E5E7EB] hover:bg-white transition-all liquid-glass"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-4xl font-bold text-[#0048ff]">0{idx + 1}</div>
                    <div className="h-px flex-1 bg-[#E5E7EB]" />
                  </div>
                  <p className="text-[#11111f] font-bold text-lg leading-snug opacity-80">{outcome}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Conversion Section - Dark & Premium */}
      <section className="py-32 bg-[#0A1128] overflow-hidden relative">
        {/* Animated Background Gradients */}
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-[#0048ff]/20 rounded-full blur-[120px]" />
        
        <div className="container-wide relative z-10 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Enable the {rail.title.replace('Rail', '')} Framework for Your Organization.
            </h2>
            <p className="text-white/60 text-xl mb-12">
              Join the future of health fintech. Active governance starts with one platform.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/register" className="w-full sm:w-auto">
                <Button 
                  size="xl" 
                  className="w-full sm:w-auto bg-[#0048ff] hover:bg-[#003bce] text-white h-20 px-12 rounded-2xl text-xl font-bold transition-all shadow-2xl shadow-[#0048ff]/40"
                >
                  Register Company <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
              </Link>
              <Link to="/demo" className="w-full sm:w-auto">
                <Button 
                  size="xl"
                  variant="outline" 
                  className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 h-20 px-12 rounded-2xl text-xl font-bold transition-all"
                >
                  Request Live Demo
                </Button>
              </Link>
            </div>
            
            <p className="text-white/20 text-sm mt-16 font-medium tracking-widest uppercase">
              Corporate Infrastructure & Services • IaaS Framework v3.0
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
